import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, Eye, Phone, Trophy, Play, Monitor,
  Settings, Sparkles, Zap, Shield, Star, ArrowRight,
  CreditCard, Gift, Users, CheckCircle, Medal,
  AlertTriangle, Info, Heart, Ban, ShieldCheck, Lock,
} from 'lucide-react';
import Button from '../components/ui/Button';
import { getPublicLeaderboard } from '../api/leaderboardApi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const css = `
@keyframes ctaPulse {
  0%, 100% { box-shadow: 0 0 20px rgba(212,175,55,0.3), 0 0 60px rgba(212,175,55,0.1); }
  50% { box-shadow: 0 0 30px rgba(212,175,55,0.5), 0 0 80px rgba(212,175,55,0.2); }
}
@keyframes disclaimerSlide {
  from { transform: translateY(-100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (max-width: 768px) {
  .h-steps { grid-template-columns: 1fr 1fr !important; }
  .h-feat { grid-template-columns: 1fr !important; }
  .h-cmp { grid-template-columns: 1fr !important; }
  .h-hero-t { font-size: 2.5rem !important; }
  .h-sec { padding: 48px 16px !important; }
  .h-hero { min-height: 80vh !important; padding: 80px 16px 40px !important; }
  .h-nav-links { gap: 12px !important; }
  .h-nav-links a:not(.h-nav-login) { display: none !important; }
  .h-op-row { flex-direction: column !important; }
  .h-faq-grid { grid-template-columns: 1fr !important; }
  .h-disclaimer-cards { grid-template-columns: 1fr !important; }
  .h-disclaimer-bar { font-size: 0.72rem !important; padding: 8px 12px !important; }
}
@media (max-width: 480px) {
  .h-steps { grid-template-columns: 1fr !important; }
  .h-hero-t { font-size: 2rem !important; }
}
`;

function formatEuro(val) {
  if (typeof val !== 'number') return String(val);
  return val.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: val < 1 ? 2 : 0, maximumFractionDigits: 2 });
}

export default function Home() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbStats, setLbStats] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    getPublicLeaderboard()
      .then((data) => {
        setLeaderboard(data.leaderboard || []);
        setLbStats(data.stats || null);
      })
      .catch(() => {});
  }, []);

  return (
    <div style={S.page}>
      <style>{css}</style>

      {/* ═══ NAVBAR ═══ */}
      <nav style={S.nav}>
        <span style={S.navBrand}>PICKBOXGAME</span>
        <div className="h-nav-links" style={S.navLinks}>
          <a href="#come-funziona" style={S.navLink}>Come funziona</a>
          <a href="#classifica" style={S.navLink}>Classifica</a>
          <a href="#organizzatori" style={S.navLink}>Per organizzatori</a>
          <a href="#prezzi" style={S.navLink}>Prezzi</a>
          <a href="mailto:emiliogrimaldi101@gmail.com" style={S.navLink}>Contattaci</a>
          <Link to="/play" style={S.navLink}>Gioca</Link>
          <Link to="/login" className="h-nav-login" style={S.navLoginBtn}>Accedi</Link>
        </div>
      </nav>

      {/* ═══ DISCLAIMER BANNER ═══ */}
      {showDisclaimer && (
        <div className="h-disclaimer-bar" style={S.disclaimerBar}>
          <div style={S.disclaimerBarInner}>
            <Info size={14} style={{ flexShrink: 0 }} />
            <span>
              <strong>Questo NON e' un gioco d'azzardo.</strong> PickBoxGame e' un gioco di intrattenimento completamente gratuito. Non si vincono premi reali in denaro. Nessuna scommessa, nessun rischio.
            </span>
            <button
              onClick={() => setShowDisclaimer(false)}
              style={S.disclaimerClose}
              aria-label="Chiudi avviso"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      <section className="h-hero" style={{ ...S.hero, paddingTop: showDisclaimer ? '120px' : '80px' }}>
        <div style={S.heroBg} />
        <motion.div
          style={S.heroContent}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div style={S.badge}><Sparkles size={13} /> Il gioco delle scatole online</div>
          <h1 className="h-hero-t" style={S.heroTitle}>PickBoxGame</h1>
          <p style={S.heroSub}>
            Il game show delle scatole ispirato ai celebri giochi televisivi italiani.
            Scegli la tua scatola, apri le altre, ricevi le offerte del Banchiere e scopri se hai fatto l'affare della vita.
          </p>
          <div style={S.heroDisclaimerInline}>
            <ShieldCheck size={13} />
            100% gratuito &middot; Nessun gioco d'azzardo &middot; Solo divertimento
          </div>
          <div style={S.heroCtas}>
            <Link to="/play" style={{ textDecoration: 'none' }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Button size="lg" style={S.ctaBtn}>
                  <Play size={18} /> Gioca gratis
                </Button>
              </motion.div>
            </Link>
            <Link to="/register" style={S.ctaLink}>
              Diventa organizzatore <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ═══ DISCLAIMER SECTION ═══ */}
      <section className="h-sec" style={{ ...S.sec, paddingTop: '32px', paddingBottom: '32px' }}>
        <motion.div
          style={S.disclaimerSection}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div style={S.disclaimerHeader}>
            <AlertTriangle size={20} style={{ color: '#e67e22' }} />
            <h3 style={S.disclaimerTitle}>Avviso importante</h3>
          </div>
          <p style={S.disclaimerText}>
            <strong>PickBoxGame</strong> e' un gioco di intrattenimento a scopo puramente ludico e ricreativo.
            <strong> Non e' un gioco d'azzardo</strong>, non prevede scommesse in denaro reale, non richiede alcun deposito
            e non e' possibile vincere premi in denaro reale. I valori mostrati durante il gioco sono fittizi e hanno
            il solo scopo di simulare l'esperienza del game show televisivo. Il gioco e' <strong>completamente gratuito</strong> per
            i giocatori e accessibile a tutti.
          </p>
          <div className="h-disclaimer-cards" style={S.disclaimerCards}>
            {[
              { I: Ban, color: '#e74c3c', t: 'Nessun azzardo', d: 'Non e\' un casinò, non ci sono scommesse ne\' puntate di denaro reale' },
              { I: Gift, color: '#2ecc71', t: 'Premi fittizi', d: 'I valori nelle scatole sono simulati — nessun premio in denaro reale viene erogato' },
              { I: Lock, color: '#3498db', t: 'Nessun deposito', d: 'Non viene richiesto alcun pagamento, carta di credito o dato bancario per giocare' },
              { I: Heart, color: '#e91e63', t: 'Solo divertimento', d: 'Un gioco pensato per divertirsi, ispirato al celebre format televisivo italiano' },
            ].map(({ I, color, t, d }, i) => (
              <div key={i} style={S.disclaimerCard}>
                <div style={{ ...S.disclaimerCardIcon, backgroundColor: `${color}10`, color }}>
                  <I size={16} />
                </div>
                <div>
                  <h4 style={S.disclaimerCardTitle}>{t}</h4>
                  <p style={S.disclaimerCardDesc}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ═══ SEO TEXT — cos'e' ═══ */}
      <section className="h-sec" style={S.sec}>
        <SectionHead title="Il gioco delle scatole" accent="online" />
        <div style={S.seoText}>
          <p>
            <strong>PickBoxGame</strong> porta l'emozione del game show direttamente nel tuo browser.
            20 scatole, ognuna associata a un segno zodiacale o un pianeta,
            contengono premi di valore diverso — da pochi centesimi fino a 300.000 euro virtuali.
          </p>
          <p>
            Il giocatore sceglie la propria scatola e poi, una alla volta, apre le altre. Dopo ogni gruppo di aperture,
            il misterioso <strong>Banchiere</strong> chiama con un'offerta: una somma in denaro virtuale per comprare la scatola
            del concorrente, oppure la proposta di uno <strong>scambio</strong> con un'altra scatola ancora chiusa.
            Accettare o rifiutare? Questa e' la domanda che tiene tutti col fiato sospeso.
          </p>
          <p>
            Che tu voglia giocare da solo per divertimento o organizzare un vero game show dal vivo
            per il tuo evento, <strong>PickBoxGame</strong> e' la piattaforma giusta.
          </p>
        </div>
      </section>

      {/* ═══ COME SI GIOCA — 4 step ═══ */}
      <section id="come-funziona" className="h-sec" style={S.sec}>
        <SectionHead title="Come si" accent="gioca" />
        <p style={S.secSub}>
          Le regole sono semplici e avvincenti. Un game show imprevedibile tutto da vivere.
        </p>
        <motion.div className="h-steps" style={S.stepsGrid} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={stagger}>
          {[
            { n: '01', I: Package, t: 'Scegli la tua scatola', d: '20 scatole con segni zodiacali e pianeti. Ognuna nasconde un premio diverso: quale sara\' la tua?' },
            { n: '02', I: Eye, t: 'Apri le altre scatole', d: 'Scopri cosa c\'era nelle altre scatole. Ogni apertura cambia le probabilita\' e la tensione sale.' },
            { n: '03', I: Phone, t: 'Il Banchiere chiama', d: 'Offerta in denaro o proposta di scambio. La scelta e\' tua: accettare o rifiutare?' },
            { n: '04', I: Trophy, t: 'Scopri il premio', d: 'Alla fine si apre la tua scatola. Hai fatto l\'affare giusto o ti sei lasciato scappare la fortuna?' },
          ].map(({ n, I, t, d }) => (
            <motion.div key={n} style={S.step} variants={fadeUp} whileHover={{ borderColor: 'rgba(212,175,55,0.2)' }}>
              <span style={S.stepN}>{n}</span>
              <div style={S.stepI}><I size={20} /></div>
              <h3 style={S.stepT}>{t}</h3>
              <p style={S.stepD}>{d}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ═══ PER ORGANIZZATORI ═══ */}
      <section id="organizzatori" className="h-sec" style={{ ...S.sec, background: 'linear-gradient(180deg, rgba(52,152,219,0.02) 0%, transparent 100%)', borderTop: '1px solid rgba(52,152,219,0.06)', borderBottom: '1px solid rgba(52,152,219,0.06)' }}>
        <div style={S.opBadge}><Shield size={13} /> Per organizzatori di eventi</div>
        <SectionHead title="Porta il game show ai tuoi" accent="eventi" />
        <p style={S.secSub}>
          Fiere, feste aziendali, team building, serate di beneficenza, matrimoni.
          Diventa il conduttore del tuo game show con una gestione partita professionale.
        </p>

        <div className="h-op-row" style={S.opRow}>
          <motion.div className="h-feat" style={S.opFeat} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={stagger}>
            {[
              { I: Monitor, t: 'Schermo Live', d: 'Link dedicato da proiettare su TV o schermo per il pubblico' },
              { I: Settings, t: 'Premi personalizzabili', d: 'Scegli tu i premi: buoni, gadget, esperienze o importi virtuali' },
              { I: Zap, t: 'Gestisci dal tablet', d: 'Apri scatole, chiama il Banchiere e gestisci le offerte in tempo reale' },
              { I: Users, t: 'Offerte e scambi', d: 'Decidi tu l\'importo dell\'offerta o proponi lo scambio delle scatole' },
            ].map(({ I, t, d }, i) => (
              <motion.div key={i} style={S.opCard} variants={fadeUp}>
                <div style={S.opIcon}><I size={18} /></div>
                <div>
                  <h4 style={S.opT}>{t}</h4>
                  <p style={S.opD}>{d}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div style={S.opSteps} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={stagger}>
            <h3 style={S.opStepsTitle}>Inizia in 3 minuti</h3>
            {[
              'Registrati gratis — nessuna carta richiesta',
              'La prima partita te la offriamo noi',
              'Configura i tuoi premi personalizzati',
              'Crea la partita e condividi il link live',
            ].map((text, i) => (
              <motion.div key={i} style={S.opStep} variants={fadeUp}>
                <div style={S.opStepN}>{i + 1}</div>
                <p style={S.opStepText}>{text}</p>
              </motion.div>
            ))}
            <Link to="/register" style={{ textDecoration: 'none', marginTop: '8px' }}>
              <Button variant="secondary" style={{ width: '100%', borderRadius: '9999px', borderColor: '#3498db', color: '#3498db' }}>
                <Shield size={14} /> Registrati gratis
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="prezzi" className="h-sec" style={S.sec}>
        <SectionHead title="Scegli il tuo" accent="piano" />
        <p style={S.secSub}>
          Gioca gratis da giocatore, oppure diventa organizzatore per portare il game show ai tuoi eventi.
        </p>
        <motion.div className="h-cmp" style={S.cmpGrid} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={stagger}>
          {/* Giocatore */}
          <motion.div style={S.cmpCard} variants={fadeUp} whileHover={{ borderColor: 'rgba(212,175,55,0.25)', y: -3 }}>
            <div style={S.cmpHead}>
              <Play size={22} style={{ color: '#D4AF37' }} />
              <div>
                <h3 style={S.cmpTitle}>Giocatore</h3>
                <span style={S.cmpPrice}>Gratis per sempre</span>
              </div>
            </div>
            <ul style={S.cmpList}>
              {[
                'Gioca subito dal browser',
                'Nessuna registrazione richiesta',
                '20 scatole con segni e pianeti',
                'Offerte del Banchiere e scambi',
                'Partite illimitate',
                'Nessun costo nascosto',
              ].map((t, i) => (
                <li key={i} style={S.cmpItem}><Star size={12} style={{ color: '#D4AF37', flexShrink: 0 }} />{t}</li>
              ))}
            </ul>
            <Link to="/play" style={{ textDecoration: 'none' }}>
              <Button style={{ width: '100%', borderRadius: '9999px' }}><Play size={14} /> Gioca ora</Button>
            </Link>
          </motion.div>

          {/* Organizzatore */}
          <motion.div style={{ ...S.cmpCard, ...S.cmpHL }} variants={fadeUp} whileHover={{ borderColor: 'rgba(52,152,219,0.4)', y: -3 }}>
            <div style={S.cmpBadge}>PRO</div>
            <div style={S.cmpHead}>
              <Shield size={22} style={{ color: '#3498db' }} />
              <div>
                <h3 style={S.cmpTitle}>Organizzatore</h3>
                <span style={{ ...S.cmpPrice, color: '#3498db' }}>9,99 / mese</span>
              </div>
            </div>
            <ul style={S.cmpList}>
              {[
                'Prima partita gratuita per provare',
                'Gestione completa dal tablet o PC',
                'Schermo live con link unico',
                'Premi e regole personalizzabili',
                'Offerte e scambi decisi da te',
                'Dashboard e storico partite',
              ].map((t, i) => (
                <li key={i} style={S.cmpItem}><Star size={12} style={{ color: '#3498db', flexShrink: 0 }} />{t}</li>
              ))}
            </ul>
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" style={{ width: '100%', borderRadius: '9999px', borderColor: '#3498db', color: '#3498db' }}>
                <Gift size={14} /> Prova gratis
              </Button>
            </Link>
            <p style={{ fontSize: '0.72rem', color: '#8888aa', textAlign: 'center', margin: 0 }}>
              Nessuna carta richiesta per la prova. Cancella quando vuoi.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ CLASSIFICA ═══ */}
      {leaderboard.length > 0 && (
        <section id="classifica" className="h-sec" style={{ ...S.sec, background: 'linear-gradient(180deg, rgba(212,175,55,0.02) 0%, transparent 100%)', borderTop: '1px solid rgba(212,175,55,0.06)', borderBottom: '1px solid rgba(212,175,55,0.06)' }}>
          <div style={S.opBadge}><Medal size={13} style={{ color: '#D4AF37' }} /> Hall of Fame</div>
          <SectionHead title="I migliori" accent="giocatori" />
          <p style={S.secSub}>
            La classifica dei premi virtuali piu' alti ottenuti dai giocatori di PickBoxGame.
          </p>

          {lbStats && (
            <div style={S.lbStatsRow}>
              <div style={S.lbStat}>
                <div style={S.lbStatValue}>{lbStats.total_games.toLocaleString('it-IT')}</div>
                <div style={S.lbStatLabel}>Partite giocate</div>
              </div>
              <div style={S.lbStat}>
                <div style={S.lbStatValue}>{formatEuro(lbStats.total_prize)}</div>
                <div style={S.lbStatLabel}>Premi virtuali totali</div>
              </div>
            </div>
          )}

          <motion.div
            style={S.lbTable}
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={stagger}
          >
            {leaderboard.slice(0, 10).map((entry) => (
              <motion.div
                key={`${entry.rank}-${entry.player_name}`}
                style={{
                  ...S.lbRow,
                  ...(entry.rank <= 3 ? S.lbRowTop : {}),
                }}
                variants={fadeUp}
              >
                <div style={{
                  ...S.lbRank,
                  color: entry.rank === 1 ? '#D4AF37' : entry.rank === 2 ? '#C0C0C0' : entry.rank === 3 ? '#CD7F32' : 'var(--color-text-muted)',
                }}>
                  {entry.rank <= 3 ? <Trophy size={16} /> : entry.rank}
                </div>
                <div style={S.lbName}>{entry.player_name}</div>
                <div style={S.lbPrize}>{formatEuro(entry.prize)}</div>
                <div style={{
                  ...S.lbBadge,
                  backgroundColor: entry.offer_accepted ? 'rgba(46,204,113,0.1)' : 'rgba(52,152,219,0.1)',
                  color: entry.offer_accepted ? '#2ecc71' : '#3498db',
                }}>
                  {entry.offer_accepted ? 'Offerta' : 'Pacco'}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <p style={S.lbDisclaimer}>
            <Info size={12} /> I premi mostrati in classifica sono virtuali e non corrispondono a vincite in denaro reale.
          </p>

          <div style={{ textAlign: 'center', marginTop: 'var(--space-md)' }}>
            <Link to="/play" style={{ textDecoration: 'none' }}>
              <Button style={{ borderRadius: '9999px' }}>
                <Play size={14} /> Gioca e scala la classifica
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* ═══ FAQ / SEO CONTENT ═══ */}
      <section className="h-sec" style={S.sec}>
        <SectionHead title="Domande" accent="frequenti" />
        <motion.div className="h-faq-grid" style={S.faqGrid} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={stagger}>
          {[
            {
              q: 'Come funziona il gioco delle scatole?',
              a: 'Il gioco si ispira ai celebri game show televisivi: 20 scatole con segni zodiacali e pianeti, le chiamate del Banchiere con offerte in denaro o proposte di scambio. Giochi dal tuo dispositivo e i premi sono virtuali!',
            },
            {
              q: 'E\' un gioco d\'azzardo?',
              a: 'Assolutamente no. PickBoxGame e\' un gioco di intrattenimento gratuito. Non si scommette denaro reale, non si effettuano depositi e non si vincono premi in denaro. I valori nelle scatole sono puramente simulati per ricreare l\'esperienza del game show televisivo. Non e\' necessario essere maggiorenni per giocare.',
            },
            {
              q: 'Posso giocare gratis?',
              a: 'Si! La modalita\' giocatore e\' completamente gratuita, senza registrazione e senza limiti. Puoi giocare quante partite vuoi direttamente dal browser. Non verranno mai richiesti dati di pagamento.',
            },
            {
              q: 'I premi sono reali?',
              a: 'No, tutti i premi mostrati nel gioco sono fittizi e virtuali. Gli importi servono esclusivamente a simulare l\'esperienza del format televisivo. PickBoxGame e\' un gioco di puro intrattenimento senza alcun valore economico reale.',
            },
            {
              q: 'Cosa include il piano Organizzatore?',
              a: 'Con l\'abbonamento da 9,99 euro al mese hai una gestione completa per le partite dal vivo: apri le scatole tu, decidi le offerte del Banchiere, proietti il gioco su uno schermo per il pubblico e personalizzi i premi virtuali.',
            },
            {
              q: 'Posso provare la modalita\' organizzatore prima di pagare?',
              a: 'Certo! Registrati gratuitamente e la prima partita e\' offerta da noi. Nessuna carta di credito richiesta per provare. Se ti piace, attivi l\'abbonamento quando vuoi.',
            },
            {
              q: 'Per quali eventi e\' adatto?',
              a: 'PickBoxGame e\' perfetto per feste aziendali, team building, sagre, fiere, serate di beneficenza, matrimoni, compleanni e qualsiasi evento dove vuoi intrattenere il pubblico con un game show interattivo.',
            },
            {
              q: 'Come funziona lo schermo live?',
              a: 'Quando crei una partita dal pannello di gestione, ottieni un link unico da aprire su un secondo schermo, TV o proiettore. Il pubblico vede le scatole aprirsi in tempo reale, le offerte del Banchiere e il premio finale — tutto virtuale e a scopo di intrattenimento.',
            },
          ].map(({ q, a }, i) => (
            <motion.div key={i} style={S.faqItem} variants={fadeUp}>
              <h3 style={S.faqQ}>{q}</h3>
              <p style={S.faqA}>{a}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ═══ CTA FINALE ═══ */}
      <section className="h-sec" style={{ ...S.sec, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={S.finalCta}
        >
          <h2 style={S.finalCtaTitle}>Pronto a fare il tuo affare?</h2>
          <p style={S.finalCtaSub}>
            Gioca subito gratis oppure registrati come organizzatore — la prima partita e' in regalo.
          </p>
          <div style={S.heroCtas}>
            <Link to="/play" style={{ textDecoration: 'none' }}>
              <Button size="lg" style={S.ctaBtn}>
                <Play size={18} /> Gioca gratis
              </Button>
            </Link>
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" size="lg" style={{ borderRadius: '9999px' }}>
                <Shield size={16} /> Diventa organizzatore
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={S.footer}>
        <div style={S.footerInner}>
          <span style={S.fBrand}>PICKBOXGAME</span>
          <div style={S.footerLinks}>
            <Link to="/play" style={S.fLink}>Gioca gratis</Link>
            <span style={S.fDot}>&middot;</span>
            <Link to="/register" style={S.fLink}>Registrati</Link>
            <span style={S.fDot}>&middot;</span>
            <Link to="/login" style={S.fLink}>Accedi</Link>
            <span style={S.fDot}>&middot;</span>
            <a href="mailto:emiliogrimaldi101@gmail.com" style={S.fLink}>Contattaci</a>
          </div>
          <p style={S.footerSeo}>
            PickBoxGame — il gioco delle scatole online ispirato ai game show televisivi.
            Gioca gratis o organizza il tuo game show per eventi, feste e team building.
          </p>

          {/* Legal disclaimer nel footer */}
          <div style={S.footerLegal}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={S.footerLegalText}>
              <strong>Disclaimer:</strong> PickBoxGame e' un gioco di intrattenimento gratuito e non costituisce in alcun modo
              un gioco d'azzardo ai sensi della normativa vigente. Non e' prevista alcuna forma di scommessa, deposito o vincita
              in denaro reale. Tutti i premi e i valori mostrati durante il gioco sono fittizi e puramente simulati.
              Il servizio non e' affiliato, sponsorizzato o approvato da alcun network televisivo o produttore di format.
              Tutti i marchi citati appartengono ai rispettivi proprietari.
            </p>
          </div>

          <span style={S.fCopy}>&copy; {new Date().getFullYear()} PickBoxGame. Tutti i diritti riservati.</span>
        </div>
      </footer>
    </div>
  );
}

function SectionHead({ title, accent }) {
  return (
    <motion.h2
      style={S.secTitle}
      initial="hidden" whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={fadeUp} transition={{ duration: 0.5 }}
    >
      {title} <span style={{ color: '#D4AF37' }}>{accent}</span>
    </motion.h2>
  );
}

/* ── Styles ── */
const S = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-bg-primary)', overflowX: 'hidden' },

  // Navbar
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 24px',
    background: 'rgba(5,5,16,0.9)', backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(212,175,55,0.06)',
  },
  navBrand: {
    fontFamily: 'var(--font-display)', fontSize: '0.85rem', fontWeight: 700,
    color: '#D4AF37', letterSpacing: '0.1em',
  },
  navLinks: { display: 'flex', alignItems: 'center', gap: '24px' },
  navLink: {
    fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: '#e0e0ee',
    textDecoration: 'none', transition: 'color 0.15s', letterSpacing: '0.02em',
  },
  navLoginBtn: {
    fontFamily: 'var(--font-display)', fontSize: '0.78rem', fontWeight: 600,
    color: '#D4AF37', textDecoration: 'none', padding: '6px 18px',
    borderRadius: '9999px', border: '1px solid rgba(212,175,55,0.25)',
    backgroundColor: 'rgba(212,175,55,0.06)', letterSpacing: '0.04em',
    transition: 'all 0.15s',
  },

  // Disclaimer bar
  disclaimerBar: {
    position: 'fixed', top: '45px', left: 0, right: 0, zIndex: 99,
    background: 'linear-gradient(90deg, rgba(230,126,34,0.12) 0%, rgba(231,76,60,0.08) 100%)',
    borderBottom: '1px solid rgba(230,126,34,0.15)',
    backdropFilter: 'blur(12px)',
    padding: '10px 24px',
    animation: 'disclaimerSlide 0.4s ease-out',
  },
  disclaimerBarInner: {
    maxWidth: '1000px', margin: '0 auto',
    display: 'flex', alignItems: 'center', gap: '10px',
    fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: '#e8c8a0',
    lineHeight: 1.5,
  },
  disclaimerClose: {
    background: 'none', border: 'none', color: '#e8c8a0', fontSize: '1.2rem',
    cursor: 'pointer', padding: '0 4px', marginLeft: 'auto', flexShrink: 0,
    opacity: 0.7, transition: 'opacity 0.15s',
  },

  // Hero disclaimer inline
  heroDisclaimerInline: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: '#8a8aa0',
    marginBottom: '24px', letterSpacing: '0.02em',
  },

  // Disclaimer section
  disclaimerSection: {
    backgroundColor: 'rgba(230,126,34,0.04)',
    border: '1px solid rgba(230,126,34,0.1)',
    borderRadius: '16px',
    padding: '28px 24px',
  },
  disclaimerHeader: {
    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
  },
  disclaimerTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700,
    color: '#e67e22', margin: 0, letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  disclaimerText: {
    fontFamily: 'var(--font-body)', fontSize: '0.88rem', color: '#c8c8dd',
    lineHeight: 1.7, margin: '0 0 20px',
  },
  disclaimerCards: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px',
  },
  disclaimerCard: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '10px', padding: '14px 12px',
    border: '1px solid rgba(255,255,255,0.04)',
  },
  disclaimerCardIcon: {
    width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  disclaimerCardTitle: {
    fontFamily: 'var(--font-display)', fontSize: '0.82rem', fontWeight: 600,
    color: 'var(--color-text-primary)', margin: '0 0 2px',
  },
  disclaimerCardDesc: {
    fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: '#a0a0b8',
    lineHeight: 1.4, margin: 0,
  },

  // Hero
  hero: {
    position: 'relative', minHeight: '85vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    padding: '80px 24px 60px', overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse 70% 50% at 50% 45%, rgba(212,175,55,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroContent: { position: 'relative', zIndex: 1, maxWidth: '700px' },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '0.8rem', fontWeight: 500, color: '#D4AF37',
    padding: '6px 16px', borderRadius: '9999px',
    backgroundColor: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.12)',
    marginBottom: '20px', fontFamily: 'var(--font-body)', letterSpacing: '0.03em',
  },
  heroTitle: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(2.8rem, 7vw, 4.5rem)',
    fontWeight: 700, color: '#D4AF37', letterSpacing: '0.06em',
    textTransform: 'uppercase', lineHeight: 1, margin: 0,
    textShadow: '0 0 50px rgba(212,175,55,0.3), 0 0 100px rgba(212,175,55,0.12)',
  },
  heroSub: {
    fontFamily: 'var(--font-body)', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
    color: '#c8c8dd', margin: '20px auto 16px',
    lineHeight: 1.7, maxWidth: '540px',
  },
  heroCtas: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' },
  ctaBtn: {
    fontSize: '1.05rem', padding: '14px 40px', borderRadius: '9999px',
    animation: 'ctaPulse 2.5s ease-in-out infinite', letterSpacing: '0.06em',
  },
  ctaLink: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: '#b0b0c8',
    textDecoration: 'none', transition: 'color 0.15s',
  },

  // SEO text block
  seoText: {
    maxWidth: '700px', margin: '24px auto 0', fontFamily: 'var(--font-body)',
    fontSize: '0.95rem', color: '#b8b8d0', lineHeight: 1.8, textAlign: 'center',
  },

  // Sections
  sec: { padding: '48px 24px', maxWidth: '1000px', margin: '0 auto' },
  secTitle: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
    fontWeight: 700, color: 'var(--color-text-primary)', textAlign: 'center',
    letterSpacing: '0.04em', textTransform: 'uppercase', margin: '0 0 8px',
  },
  secSub: {
    fontFamily: 'var(--font-body)', fontSize: '0.95rem', color: '#c8c8dd',
    textAlign: 'center', lineHeight: 1.6, maxWidth: '540px', margin: '0 auto 36px',
  },

  // Steps
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginTop: '32px' },
  step: {
    backgroundColor: 'var(--color-bg-card)', borderRadius: '12px', padding: '20px 18px',
    border: '1px solid rgba(212,175,55,0.06)', position: 'relative', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', gap: '8px', transition: 'border-color 0.2s',
  },
  stepN: {
    fontFamily: 'var(--font-display)', fontSize: '2.2rem', fontWeight: 700,
    color: 'rgba(212,175,55,0.05)', position: 'absolute', top: '8px', right: '12px', lineHeight: 1,
  },
  stepI: {
    width: '36px', height: '36px', borderRadius: '8px',
    backgroundColor: 'rgba(212,175,55,0.08)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#D4AF37',
  },
  stepT: { fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 },
  stepD: { fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: '#c8c8dd', lineHeight: 1.5, margin: 0 },

  // Operator section
  opBadge: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    fontSize: '0.8rem', fontWeight: 500, color: '#3498db', marginBottom: '12px',
    fontFamily: 'var(--font-body)', letterSpacing: '0.03em',
  },
  opRow: { display: 'flex', gap: '32px', marginTop: '36px', alignItems: 'flex-start' },
  opFeat: { flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  opCard: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    backgroundColor: 'var(--color-bg-card)', borderRadius: '10px', padding: '16px',
    border: '1px solid rgba(52,152,219,0.06)',
  },
  opIcon: {
    width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
    backgroundColor: 'rgba(52,152,219,0.08)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#3498db',
  },
  opT: { fontFamily: 'var(--font-display)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0 0 2px' },
  opD: { fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: '#c8c8dd', margin: 0, lineHeight: 1.4 },
  opSteps: {
    width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px',
    backgroundColor: 'var(--color-bg-card)', borderRadius: '12px', padding: '24px 20px',
    border: '1px solid rgba(212,175,55,0.06)',
  },
  opStepsTitle: {
    fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600,
    color: '#D4AF37', margin: '0 0 4px', letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  opStep: { display: 'flex', alignItems: 'center', gap: '12px' },
  opStepN: {
    width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #D4AF37, #F0D060)',
    color: '#1a1a2e', fontFamily: 'var(--font-display)', fontSize: '0.8rem',
    fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  opStepText: { fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: '#c8c8dd', margin: 0, lineHeight: 1.4 },

  // Comparison / Pricing
  cmpGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', maxWidth: '720px', margin: '32px auto 0' },
  cmpCard: {
    backgroundColor: 'var(--color-bg-card)', borderRadius: '16px', padding: '28px 24px',
    border: '1px solid rgba(212,175,55,0.08)', display: 'flex',
    flexDirection: 'column', gap: '20px', position: 'relative', transition: 'all 0.2s',
  },
  cmpHL: { border: '1px solid rgba(52,152,219,0.15)', background: 'linear-gradient(180deg, rgba(52,152,219,0.02) 0%, var(--color-bg-card) 100%)' },
  cmpBadge: {
    position: 'absolute', top: '-10px', right: '20px',
    fontSize: '0.65rem', fontWeight: 700, color: '#fff', padding: '3px 12px',
    borderRadius: '9999px', background: 'linear-gradient(135deg, #2980b9, #3498db)',
    letterSpacing: '0.08em', fontFamily: 'var(--font-display)',
  },
  cmpHead: { display: 'flex', alignItems: 'center', gap: '14px' },
  cmpTitle: { fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 },
  cmpPrice: { fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: '#D4AF37' },
  cmpList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 },
  cmpItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: '#c8c8dd',
  },

  // Leaderboard
  lbStatsRow: {
    display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '28px', flexWrap: 'wrap',
  },
  lbStat: {
    textAlign: 'center', padding: '16px 28px',
    backgroundColor: 'var(--color-bg-card)', borderRadius: '12px',
    border: '1px solid rgba(212,175,55,0.08)',
  },
  lbStatValue: {
    fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: '#D4AF37',
  },
  lbStatLabel: {
    fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: '#8888aa', marginTop: '4px',
    letterSpacing: '0.05em', textTransform: 'uppercase',
  },
  lbTable: {
    maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '6px',
  },
  lbRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 16px', borderRadius: '10px',
    backgroundColor: 'var(--color-bg-card)', border: '1px solid rgba(255,255,255,0.03)',
  },
  lbRowTop: {
    border: '1px solid rgba(212,175,55,0.12)',
    backgroundColor: 'rgba(212,175,55,0.03)',
  },
  lbRank: {
    fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 700,
    width: '32px', textAlign: 'center', flexShrink: 0,
  },
  lbName: {
    flex: 1, fontFamily: 'var(--font-body)', fontSize: '0.88rem',
    color: 'var(--color-text-primary)', fontWeight: 500,
  },
  lbPrize: {
    fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600,
    color: '#D4AF37', whiteSpace: 'nowrap',
  },
  lbBadge: {
    fontSize: '0.65rem', padding: '2px 8px', borderRadius: '9999px',
    fontFamily: 'var(--font-display)', letterSpacing: '0.04em',
    flexShrink: 0,
  },
  lbDisclaimer: {
    fontFamily: 'var(--font-body)', fontSize: '0.72rem', color: '#6a6a80',
    textAlign: 'center', marginTop: '16px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  },

  // FAQ
  faqGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '32px' },
  faqItem: {
    backgroundColor: 'var(--color-bg-card)', borderRadius: '12px', padding: '20px',
    border: '1px solid rgba(212,175,55,0.06)',
  },
  faqQ: {
    fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600,
    color: 'var(--color-text-primary)', margin: '0 0 8px', lineHeight: 1.4,
  },
  faqA: {
    fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: '#b8b8d0',
    lineHeight: 1.6, margin: 0,
  },

  // Final CTA
  finalCta: {
    padding: '40px 24px',
    backgroundColor: 'rgba(212,175,55,0.03)',
    borderRadius: '20px',
    border: '1px solid rgba(212,175,55,0.08)',
  },
  finalCtaTitle: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    fontWeight: 700, color: '#D4AF37', margin: '0 0 12px',
    letterSpacing: '0.04em',
  },
  finalCtaSub: {
    fontFamily: 'var(--font-body)', fontSize: '1rem', color: '#c8c8dd',
    margin: '0 0 28px', lineHeight: 1.6,
  },

  // Footer
  footer: {
    borderTop: '1px solid rgba(212,175,55,0.05)', padding: '40px 24px',
  },
  footerInner: {
    maxWidth: '600px', margin: '0 auto', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  footerLinks: {
    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center',
  },
  footerSeo: {
    fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: '#6a6a80',
    lineHeight: 1.6, margin: '8px 0 0', maxWidth: '460px',
  },
  footerLegal: {
    display: 'flex', alignItems: 'flex-start', gap: '8px',
    marginTop: '16px', padding: '16px',
    backgroundColor: 'rgba(230,126,34,0.04)',
    border: '1px solid rgba(230,126,34,0.08)',
    borderRadius: '10px', maxWidth: '540px',
    color: '#8a7a60',
  },
  footerLegalText: {
    fontFamily: 'var(--font-body)', fontSize: '0.68rem', color: '#8a8a9a',
    lineHeight: 1.6, margin: 0, textAlign: 'left',
  },
  fBrand: { fontFamily: 'var(--font-display)', fontSize: '0.75rem', fontWeight: 700, color: '#D4AF37', letterSpacing: '0.08em' },
  fLink: { fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: '#b0b0c8', textDecoration: 'none' },
  fDot: { color: 'rgba(255,255,255,0.1)', fontSize: '0.7rem' },
  fCopy: { fontFamily: 'var(--font-body)', fontSize: '0.7rem', color: '#6a6a80' },
};
